import { Injectable, Injector } from '@angular/core';
import { ProjectControllerService } from '@app/generated/service';
import { AbstractWorkOrderDataService } from '@app/work-order/abstract-work-order-data.service';
import { StatusCodeDto, WorkOrderDetailsDto } from '@app/generated/model';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { TOGGLE_FAVOURITE_STATUS } from '@app/actions';
import { BehaviorSubject } from 'rxjs';
import { distinct, map } from 'rxjs/operators';
import { WorkOrderObjectService } from '@app/work-order/objects/work-order-object.service';

@Injectable({
  providedIn: 'root',
})
export class StatusCodesService extends AbstractWorkOrderDataService<StatusCodeDto[]> {
  private filteredStatusCodes$ = new BehaviorSubject<StatusCodeDto[]>([]);

  constructor(
    private projectController: ProjectControllerService,
    private workOrderObjectService: WorkOrderObjectService,
    injector: Injector,
  ) {
    super(injector, 'STATUS_CODES');
    this.filterStatusCodesBasedOnSelection();
  }

  private filterStatusCodesBasedOnSelection() {
    this.workOrderObjectService
      .getSelectedObjects()
      .pipe(
        map(
          // Use object's specific status-id's or fallback to all statuses for the project
          (objectsMap) =>
            new Set(
              [...objectsMap.values()]
                .map((obj) =>
                  obj.objectStatusIds && obj.objectStatusIds.length
                    ? obj.objectStatusIds
                    : this.get().map((sc) => sc.id),
                )
                .flat(),
            ),
        ),
        distinct(),
      )
      .subscribe((availableStatuses) => {
        if (this.get()) {
          this.filteredStatusCodes$.next(this.get().filter((statusCode) => availableStatuses.has(statusCode.id)));
        }
      });
  }

  public getFilteredStatusCodes() {
    return this.filteredStatusCodes$;
  }

  public load(): void {}

  public toggleFavouriteStatus(objectStatusId: string) {
    const workOrder = this.getParent() as WorkOrderDetailsDto;
    const payload = {
      projectId: workOrder.projectId,
      statusId: objectStatusId,
    };
    return this.changeLogService.publish(TOGGLE_FAVOURITE_STATUS, payload, [workOrder.id]);
  }

  protected apply(objects: StatusCodeDto[], event: ChangeLogEvent) {
    switch (event.type) {
      case TOGGLE_FAVOURITE_STATUS: {
        const data = this.get();
        const status = data[data.map((obj) => obj.id).indexOf(event.content.statusId)];
        status.isFavourite = !!status.isFavourite;
        this.setData(data);
      }
    }
  }
}
